import React, { useState, useEffect, useRef } from "react";
import { useUser } from "../../context/UserContext";
import Login from "../../components/Login";
import MapSidebar from "./MapSidebar";
import MapControls from "./MapControls";
import UnlockPreview from "./UnlockPreview";
import {
  containerStyle,
  center,
  mapOptions,
  fetchMarkers,
  fetchAllMarkers,
  normalizeFilterValue,
  // addCommentToMarker,
  getMarkerWithComments,
} from "./markersData";
import toast from "react-hot-toast";
import AddLocationModal from "./AddLocationModal";
import StripeCheckout from "../StripeCheckout";
import { db } from "../../firebase/config";
import { collection, getDocs, query, where, addDoc, doc, updateDoc, getDoc } from "firebase/firestore";
import {
  GoogleMap,
  Marker,
  InfoWindow,
  Circle,
  useJsApiLoader,
} from "@react-google-maps/api";
import LocationInfoWindow from "./LocationInfoWindow";
import { X } from "lucide-react";

const VERIFICATION_COLORS = {
  fresh: "#2FB344",
  aging: "#F2C94C",
  stale: "#FF8000",
};

const getMarkerArtwork = (type, color) =>
  type === "premium"
    ? `
    <svg xmlns="http://www.w3.org/2000/svg" width="42" height="48" viewBox="0 0 42 48">
      <path d="M21 1C10 1 1 10 1 21c0 13.8 20 26 20 26s20-12.2 20-26C41 10 32 1 21 1Z" fill="${color}" stroke="#FFFFFF" stroke-width="3"/>
      <circle cx="21" cy="21" r="11" fill="#000000"/>
      <path d="m21 12 2.4 6.2 6.6.4-5.1 4.2 1.7 6.4-5.6-3.6-5.6 3.6 1.7-6.4-5.1-4.2 6.6-.4L21 12Z" fill="${color}"/>
    </svg>`
    : `
    <svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
      <path d="M18 1C8.6 1 1 8.6 1 18c0 12.2 17 25 17 25s17-12.8 17-25C35 8.6 27.4 1 18 1Z" fill="${color}" stroke="#000000" stroke-width="2"/>
      <circle cx="18" cy="18" r="7" fill="#000000" stroke="#FFFFFF" stroke-width="1.5"/>
      <path d="M18 8v5M18 23v5M8 18h5M23 18h5" stroke="#FFFFFF" stroke-width="1.5"/>
    </svg>`;

const markerDataUri = (type, verificationStatus) => {
  const color = VERIFICATION_COLORS[verificationStatus] || VERIFICATION_COLORS.stale;
  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(
    getMarkerArtwork(type, color)
  )}`;
};

export default function MainMap() {
  const [search, setSearch] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth >= 768);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [filters, setFilters] = useState({
    area: "all",
    type: "all",
    verification: "all",
  });
  const [selected, setSelected] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [newComment, setNewComment] = useState("");
  const [markers, setMarkers] = useState([]);
  const [userUnlockedMarkers, setUserUnlockedMarkers] = useState([]);
  const { user, loading: userLoading } = useUser();
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [circleCenter, setCircleCenter] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [markersInCircle, setMarkersInCircle] = useState([]);
  const [showUnlockPreview, setShowUnlockPreview] = useState(false);
  const [addingComment, setAddingComment] = useState(false);
  const [circleRadius] = useState(30000);
  const [showCheckout, setShowCheckout] = useState(false);
  const [mapClickForCoordinates, setMapClickForCoordinates] = useState(false);
  const [showMapClickOverlay, setShowMapClickOverlay] = useState(false);

  const mapRef = useRef();
  const lastTapRef = useRef({ time: 0, lat: 0, lng: 0 });

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyAhxyJTODlgtLM2AZuiBG8mkT9DZdYOJs0",
  });

  const fetchUserUnlocks = async (userId) => {
    try {
      const unlocksRef = collection(db, "Unlocks");
      const q = query(unlocksRef, where("userId", "==", userId));

      const querySnapshot = await getDocs(q);

      const unlockedMarkerIds = [];
      querySnapshot.forEach((doc) => {
        const unlockData = doc.data();
        if (unlockData.markersUnlocked && unlockData.userId === userId) {
          unlockedMarkerIds.push(...unlockData.markersUnlocked);
        }
      });

      return unlockedMarkerIds;
    } catch (error) {
      console.error("Error fetching user unlocks:", error);
      return [];
    }
  };

  useEffect(() => {
    loadMarkers();

    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [user]);

  useEffect(() => {
    if (!user?.premium && filters.verification !== "all") {
      setFilters((current) => ({ ...current, verification: "all" }));
    }
  }, [filters.verification, user?.premium]);

  const handleAddLocation = () => {
    if (!user) {
      toast.error("Please login to add locations");
      return;
    }
    setIsAddModalOpen(true);
  };

  const loadMarkers = async () => {
    try {
      let unlockedIds = [];
      if (user) {
        unlockedIds = await fetchUserUnlocks(user.uid);
        setUserUnlockedMarkers(unlockedIds);
      }

      const markersData = await fetchMarkers(user, unlockedIds);
      setMarkers(markersData);

    } catch (error) {
      console.error("Error loading markers:", error);
      toast.error("Failed to load locations");
    }
  };
  

  const unlockMarkers = async () => {
    if (!user || markersInCircle.length === 0) return;

    const pointsPerMarker = 10;
    const totalCost = markersInCircle.length * pointsPerMarker;

    try {
      const userDocRef = doc(db, "user1", user.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        toast.error("User data not found");
        return;
      }

      const userData = userDoc.data();
      const currentPoints = userData.points || 0;

      if (currentPoints < totalCost) {
        toast.error(
          `Insufficient points. You need ${totalCost} points but have only ${currentPoints}`
        );
        return;
      }

      await updateDoc(userDocRef, {
        points: currentPoints - totalCost,
      });

      const unlockData = {
        userId: user.uid,
        userEmail: user.email,
        centerLat: circleCenter.lat,
        centerLng: circleCenter.lng,
        radius: circleRadius,
        markersUnlocked: markersInCircle.map((marker) => marker.id),
        pointsSpent: totalCost,
        unlockedAt: new Date().toISOString(),
        expires: null,
      };

      await addDoc(collection(db, "Unlocks"), unlockData);

      const newUnlockedIds = markersInCircle.map((marker) => marker.id);
      setUserUnlockedMarkers((prev) => [...prev, ...newUnlockedIds]);

      const updatedMarkers = await fetchMarkers(user, [
        ...userUnlockedMarkers,
        ...newUnlockedIds,
      ]);
      setMarkers(updatedMarkers);

      toast.success(
        `Unlocked ${markersInCircle.length} locations for ${totalCost} points!`
      );

      setIsDrawing(false);
      setCircleCenter(null);
      setShowUnlockPreview(false);
      setMarkersInCircle([]);
    } catch (error) {
      console.error("Error unlocking markers:", error);
      toast.error("Failed to unlock locations");
    }
  };

  const handleLocationAdded = () => {
    loadMarkers();
  };

  const calculateMarkersInCircle = async (center, radius) => {
    const earthRadius = 6371000;

    try {
      const allMarkers = await fetchAllMarkers();
      const unlockedIds = user ? await fetchUserUnlocks(user.uid) : [];

      const premiumMarkersToUnlock = allMarkers.filter(
        (marker) => marker.is_premium && !unlockedIds.includes(marker.id)
      );

      const markersInside = premiumMarkersToUnlock.filter((marker) => {
        const lat1 = (center.lat * Math.PI) / 180;
        const lat2 = (marker.lat * Math.PI) / 180;
        const deltaLat = ((marker.lat - center.lat) * Math.PI) / 180;
        const deltaLng = ((marker.lng - center.lng) * Math.PI) / 180;

        const a =
          Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
          Math.cos(lat1) *
            Math.cos(lat2) *
            Math.sin(deltaLng / 2) *
            Math.sin(deltaLng / 2);
        const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
        const distance = earthRadius * c;

        return distance <= radius;
      });

      return markersInside;
    } catch (error) {
      console.error("Error calculating markers in circle:", error);
      return [];
    }
  };

 
const handleMapClick = async (event) => {
  const clickedLat = event.latLng.lat();
  const clickedLng = event.latLng.lng();

  if (isMobile && !isDrawing && !mapClickForCoordinates) {
    const now = Date.now();
    const lastTap = lastTapRef.current;
    const nearbyTap =
      Math.abs(clickedLat - lastTap.lat) < 0.03 &&
      Math.abs(clickedLng - lastTap.lng) < 0.03;

    if (now - lastTap.time < 350 && nearbyTap && mapRef.current) {
      mapRef.current.panTo({ lat: clickedLat, lng: clickedLng });
      mapRef.current.setZoom(Math.min((mapRef.current.getZoom() || 7) + 1, 20));
      lastTapRef.current = { time: 0, lat: 0, lng: 0 };
      setSelected(null);
      return;
    }

    lastTapRef.current = { time: now, lat: clickedLat, lng: clickedLng };
  }

  if (selected && !isDrawing) {
    setSelected(null);
    return;
  }

  if (mapClickForCoordinates) {
    setFormDataFromMapClick(clickedLat, clickedLng);
    setMapClickForCoordinates(false);
    setIsAddModalOpen(true);
    toast.success(`Coordinates set: ${clickedLat.toFixed(6)}, ${clickedLng.toFixed(6)}`);
    return;
  }

  if (!isDrawing) return;

  setCircleCenter({ lat: clickedLat, lng: clickedLng });

  const markersInside = await calculateMarkersInCircle(
    { lat: clickedLat, lng: clickedLng },
    30000
  );

  setMarkersInCircle(markersInside);
  setShowUnlockPreview(true);
};

const setFormDataFromMapClick = (lat, lng) => {
  window.mapClickCoordinates = { lat, lng };
};

const startMapClickForCoordinates = () => {
  setIsAddModalOpen(false);
  setMapClickForCoordinates(true);
  setShowMapClickOverlay(true);
};

const cancelMapClickProcess = () => {
  setMapClickForCoordinates(false);
  setShowMapClickOverlay(false);
};

  const startDrawing = () => {
    if (!user) {
      alert("Please login to unlock locations");
      return;
    }

    setIsDrawing(true);
    setCircleCenter(null);
    setShowUnlockPreview(false);
    setSelected(null);
  };

  const cancelDrawing = () => {
    setIsDrawing(false);
    setCircleCenter(null);
    setShowUnlockPreview(false);
    setMarkersInCircle([]);
  };

  const handleUnlockMarkers = async () => {
    const success = await unlockMarkers(
      markersInCircle,
      circleCenter,
      30000,
      userUnlockedMarkers
    );
    if (success) {
      setIsDrawing(false);
      setCircleCenter(null);
      setShowUnlockPreview(false);
      setMarkersInCircle([]);
    }
  };

  const onMapLoad = (map) => {
    mapRef.current = map;
  };

  const zoomMap = (direction) => {
    if (!mapRef.current) return;
    const currentZoom = mapRef.current.getZoom() || 7;
    mapRef.current.setZoom(
      Math.max(2, Math.min(20, currentZoom + direction))
    );
  };

  const handleMarkerHover = async (marker) => {
    if (isMobile) setSidebarOpen(false);
    try {
      const freshMarkerData = await getMarkerWithComments(marker.id);
      if (freshMarkerData) {
        setSelected(freshMarkerData);
      } else {
        setSelected(marker);
      }
      setCurrentImageIndex(0);
      setNewComment("");
    } catch (error) {
      console.error("Error fetching marker details:", error);
      setSelected(marker);
    }
  };

  const nextImage = (e) => {
    e.stopPropagation();
    if (selected && selected.images) {
      setCurrentImageIndex((prev) =>
        prev === selected.images.length - 1 ? 0 : prev + 1
      );
    }
  };

  const prevImage = (e) => {
    e.stopPropagation();
    if (selected && selected.images) {
      setCurrentImageIndex((prev) =>
        prev === 0 ? selected.images.length - 1 : prev - 1
      );
    }
  };

  // const handleAddComment = async (e) => {
  //   e.stopPropagation();
  //   if (!newComment.trim()) {
  //     toast.error("Please enter a comment");
  //     return;
  //   }

  //   if (!user) {
  //     toast.error("Please login to add a comment");
  //     return;
  //   }

  //   setAddingComment(true);

  //   try {
  //     await addCommentToMarker(
  //       selected.id,
  //       newComment,
  //       user.uid,
  //       user.name || user.email,
  //       user.email
  //     );

  //     const updatedMarker = await getMarkerWithComments(selected.id);
  //     if (updatedMarker) {
  //       setSelected(updatedMarker);
  //     }

  //     loadMarkers();

  //     toast.success("Comment added successfully!");
  //     setNewComment("");
  //   } catch (error) {
  //     console.error("Error adding comment:", error);
  //     toast.error("Failed to add comment");
  //   } finally {
  //     setAddingComment(false);
  //   }
  // };

  const handleDeleteRequest = (e) => {
    e.stopPropagation();
    if (
      window.confirm(
        "Are you sure you want to request deletion of this location?"
      )
    ) {
      toast.success("Deletion request submitted for review");
    }
  };

  const openInGoogleMaps = (e, lat, lng) => {
    e.stopPropagation();
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, "_blank");
  };

  const openInArcGIS = (e, lat, lng) => {
    e.stopPropagation();
    window.open(
      `https://www.arcgis.com/home/webmap/viewer.html?center=${lng},${lat}&level=10`,
      "_blank"
    );
  };

  const circleOptions = {
    fillColor: "#FF8000",
    fillOpacity: 0.2,
    strokeColor: "#FF8000",
    strokeOpacity: 0.8,
    strokeWeight: 2,
  };

  const filteredMarkers = markers.filter((marker) => {
    const queryValue = normalizeFilterValue(search);
    const matchesSearch = [marker.name, marker.type, marker.area]
      .filter(Boolean)
      .some((value) => normalizeFilterValue(value).includes(queryValue));
    const matchesArea =
      filters.area === "all" ||
      normalizeFilterValue(marker.area) === filters.area;
    const matchesType =
      filters.type === "all" ||
      normalizeFilterValue(marker.type) === filters.type;
    const matchesVerification =
      !user?.premium ||
      filters.verification === "all" ||
      marker.verificationStatus === filters.verification;

    return matchesSearch && matchesArea && matchesType && matchesVerification;
  });

  const markersToShow = user?.premium
    ? filteredMarkers
    : filteredMarkers.filter(
        (marker) =>
          !marker.is_premium || userUnlockedMarkers.includes(marker.id)
      );

  if (userLoading) {
    return (
      <div className="h-screen bg-black flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return (
    <div className="croatoan-shell flex h-screen bg-black text-white overflow-hidden">
      <MapSidebar
        sidebarOpen={sidebarOpen}
        onCloseSidebar={() => setSidebarOpen(false)}
        search={search}
        onSearchChange={(e) => setSearch(e.target.value)}
        onMarkerHover={handleMarkerHover}
        onStartDrawing={startDrawing}
        isDrawing={isDrawing}
        user={user}
        userUnlockedMarkers={userUnlockedMarkers}
        onAddLocation={handleAddLocation}
        onShowCheckout={() => setShowCheckout(true)}
        filters={filters}
        onFiltersChange={setFilters}
      />

      {isMobile && sidebarOpen && (
        <button
          type="button"
          className="mobile-sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-label="Close location menu and return to the map"
        />
      )}

      <main className="croatoan-map-pane flex-1 relative">
        <MapControls
          sidebarOpen={sidebarOpen}
          onToggleSidebar={() => setSidebarOpen(!sidebarOpen)}
          isDrawing={isDrawing}
          search={search}
          onSearchChange={(e) => setSearch(e.target.value)}
          onCancelDrawing={cancelDrawing}
          onAddLocation={handleAddLocation}
          onZoomIn={() => zoomMap(1)}
          onZoomOut={() => zoomMap(-1)}
        />

        {isLoaded && (
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={7}
            options={{
              ...mapOptions,
              disableDoubleClickZoom: isMobile,
              gestureHandling: "greedy",
            }}
            onClick={handleMapClick}
            onLoad={onMapLoad}
          >
            {markersToShow.map((marker) => (
              <Marker
                key={marker.id}
                position={{ lat: marker.lat, lng: marker.lng }}
                icon={{
                  url: markerDataUri(
                    marker.is_premium ? "premium" : "public",
                    marker.verificationStatus
                  ),
                  scaledSize: new window.google.maps.Size(
                    marker.is_premium ? 42 : 36,
                    marker.is_premium ? 48 : 44
                  ),
                }}
                onClick={() =>
                  handleMarkerHover({
                    ...marker,
                    category: marker.is_premium
                      ? "Premium Location"
                      : "Public Location",
                  })
                }
              />
            ))}

            {circleCenter && (
              <Circle
                center={circleCenter}
                radius={circleRadius}
                options={circleOptions}
              />
            )}

            {selected && !isMobile && (
              <InfoWindow
                position={{ lat: selected.lat, lng: selected.lng }}
                onCloseClick={() => setSelected(null)}
                options={{
                  pixelOffset: new window.google.maps.Size(0, -40),
                  maxWidth: 350,
                }}
              >
                <LocationInfoWindow
                  selected={selected}
                  currentImageIndex={currentImageIndex}
                  newComment={newComment}
                  onClose={() => setSelected(null)}
                  onNextImage={nextImage}
                  onPrevImage={prevImage}
                  // onAddComment={handleAddComment}
                  onDeleteRequest={handleDeleteRequest}
                  onOpenGoogleMaps={openInGoogleMaps}
                  onOpenArcGIS={openInArcGIS}
                  onCommentChange={(e) => setNewComment(e.target.value)}
                  addingComment={addingComment}
                  user={user}
                />
              </InfoWindow>
            )}
          </GoogleMap>
        )}

        {selected && isMobile && (
          <aside className="mobile-location-drawer" aria-label="Location details">
            <div className="mobile-location-drawer__header">
              <span>Location details</span>
              <button
                type="button"
                onClick={() => setSelected(null)}
                aria-label="Close location details"
              >
                <X size={20} />
              </button>
            </div>
            <LocationInfoWindow
              selected={selected}
              currentImageIndex={currentImageIndex}
              newComment={newComment}
              onClose={() => setSelected(null)}
              onNextImage={nextImage}
              onPrevImage={prevImage}
              onDeleteRequest={handleDeleteRequest}
              onOpenGoogleMaps={openInGoogleMaps}
              onOpenArcGIS={openInArcGIS}
              onCommentChange={(e) => setNewComment(e.target.value)}
              addingComment={addingComment}
              user={user}
            />
          </aside>
        )}

        <div className="map-brand-plaque" aria-hidden="true">
          <strong>Croatoan</strong>
          <span>Satellite map</span>
        </div>

        <div className="map-verification-legend" aria-label="Marker verification legend">
          <span><i className="legend-dot legend-dot--fresh" />Fresh · &lt;60d</span>
          <span><i className="legend-dot legend-dot--aging" />Review · 60–119d</span>
          <span><i className="legend-dot legend-dot--stale" />Stale · 120d+</span>
        </div>

        { showUnlockPreview && (
          <UnlockPreview
            markersInCircle={markersInCircle}
            onUnlockMarkers={handleUnlockMarkers}
            onCancelDrawing={cancelDrawing}
          />
        )}

        <AddLocationModal
          isOpen={isAddModalOpen}
          onClose={() => setIsAddModalOpen(false)}
          user={user}
          onLocationAdded={handleLocationAdded}
          onRequestMapClick={startMapClickForCoordinates}
          // mapClickEnabled={mapClickForCoordinates}
        />

        {showCheckout && (
          <StripeCheckout onClose={() => setShowCheckout(false)} />
        )}

        {showMapClickOverlay && (
  <div className="absolute inset-0 bg-black bg-opacity-70 flex items-center justify-center z-40">
    <div className="classified-coordinate-prompt">
      <div className="coordinate-prompt-code">Add a location</div>
      <h3 className="text-xl font-bold text-white mb-2">Choose a point</h3>
      <p className="text-gray-300 mb-6">
        Tap anywhere on the satellite map to set the location coordinates.
      </p>
      <div className="flex gap-3 justify-center">
        <button
          onClick={() => {
            setShowMapClickOverlay(false);
            setMapClickForCoordinates(true);
            toast.success("Tap the map to set the location");
          }}
          className="classified-primary-button px-6 py-2"
        >
          Okay
        </button>
        <button
          onClick={cancelMapClickProcess}
          className="coordinate-prompt-cancel px-6 py-2 rounded-lg font-medium"
        >
          Cancel
        </button>
      </div>
    </div>
  </div>
)}
      </main>
    </div>
  );
}
